import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from 'src/app/common/sharedServices/user/user.service';
import { EventiService } from './eventi.service';
import { BehaviorSubject, Subscription, lastValueFrom, timer } from 'rxjs';
import { Spesa, TipiSpesaResponse, TipoSpesa } from 'src/app/common/models/Spesa';
import { SpeseSqlAdapterService } from './spese-sql-adapter.service';
import { EventoDelResponse, EventoUpdResponse } from 'src/app/common/models/Evento';
import { AppUser } from 'src/app/common/models/Utente';

@Injectable({
  providedIn: 'root'
})
export class SpeseService {
  private readonly GET_TIPI_SPESA_ROUTE = '/immto_wsr_prodottispese_get';
  private readonly UPDATE_SPESA_ROUTE = '/immto_wsr_eventi_insupd';
  private readonly DELETE_SPESA_ROUTE = '/immto_wsr_eventi_delete';
  private readonly INSUPDDEL_SPESA_ROUTE = '/immto_wsr_eventiall_insupddel';

  _cachedWsResult = new BehaviorSubject<Spesa[]>([]);
  private updateSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private speseOfflineService: SpeseSqlAdapterService, // Aggiornamento dell'anagrafica
    private userService: UserService, // Sorgente dei dati dell'utente necessari per la chiamata
    private eventiService: EventiService
  ) {  }

  async downloadSpeseDiInterventi(idInterventi: number[]): Promise<number> {
    let numeroRecords = 0;
    try {
      await this.speseOfflineService.rimuoviSpese();
      let records: Spesa[];
      for (const idIntervento of idInterventi) {
        records = await this.fetchRecordsFromServer(idIntervento);
        if (records && records.length > 0) {
          await this.speseOfflineService.inserisciSpese(records);
          numeroRecords += records.length;
        }
      }
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il DOWNLOAD delle spese degli interventi `, error);
      return 0;
    }
    console.debug(`Scaricati ${numeroRecords} records spese di interventi`);
    return numeroRecords;
  }

  async fetchRecordsFromServer(idIntervento: number): Promise<Spesa[]> {
      const eventi = await this.eventiService.fetchEventiOnline(idIntervento, 'S');
      const spese = this.getSpeseFromEventi(eventi);
      return spese;
  }

  private getSpeseFromEventi(eventi: any[]){
    const spese: Spesa[] = [];
    eventi.forEach(e => {
      spese.push(
        {
          aziCdazi: e.aziCdazi,
          idProdotto: e.idProdotto,
          codiceProdotto: e.codiceProdotto,
          descrizioneProdotto: e.descrizioneProdotto,
          costoUnitario: e.costoUnitario,
          quantita: e.quantita,
          idIntervento: e.idIntervento,
          idRigoMovimentazione: e.idEvento,
          codiceOperatore: e.codiceOperatore,
          nomeOperatore: e.nomeOperatore,
          data: new Date(e.annoAttivita, e.meseAttivita - 1, e.giornoAttivita),
          allegati: [],
          numeroAllegati: e.numeroAllegati
        }
      )
    })
    return spese;
  }

  async fetchTipiSpesaFromServer(){
    const { server, aziCdazi, token } = await this.userService.getDatiUtente();
    let completeUrl = `${server}${this.GET_TIPI_SPESA_ROUTE}`;

    const body = {
      aziCdazi: aziCdazi,
      token: token,
    }
    const response = await lastValueFrom(this.http.post<TipiSpesaResponse>(completeUrl,body))
    return response && response['st'] == 0 ? response.SDTwsrProdottiSpese : [];
  }

  async downloadTipiSpese(): Promise<number> {
    let numeroRecords = 0;
    try {
      await this.speseOfflineService.rimuoviTipiSpese();

      const records = await this.fetchTipiSpesaFromServer();
      if (records && records.length > 0) {
        await this.speseOfflineService.inserisciTipiSpese(records);
        numeroRecords += records.length;
      }
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il DOWNLOAD dei tipi spese degli interventi `, error);
      return 0;
    }
    console.debug(`Scaricati ${numeroRecords} records tipi spese`);
    return numeroRecords;
  }

  async uploadSpesa(spesa: Spesa, isCreazione: boolean): Promise<any> {
    const userData = await this.userService.getDatiUtente();

    const completeUrl = `${userData.server}${this.UPDATE_SPESA_ROUTE}`;
    const body = {
      'aziCdazi': userData.aziCdazi,
      'token': userData.token,
      'codiceAddetto': userData.putSigla,
      'idIntervento': spesa.idIntervento,
      'SDTwsrEventiJson': JSON.stringify([{   //In InteGRa "eventi", "ricambi" e "spese" sono la stessa entità
        aziCdazi: spesa.aziCdazi || userData.aziCdazi,
        idProdotto: spesa.idProdotto,
        codiceProdotto: spesa.codiceProdotto,
        descrizioneProdotto: spesa.descrizioneProdotto,
        idIntervento: spesa.idIntervento,
        quantita: spesa.quantita,
        costoUnitario: spesa.costoUnitario,
        descrizione: spesa.descrizioneProdotto,
        idEvento: isCreazione ? 0 : spesa.idRigoMovimentazione,
        codiceOperatore: spesa.codiceOperatore,
        giornoAttivita: spesa.data.getDate(),
        meseAttivita: spesa.data.getMonth() + 1,
        annoAttivita: spesa.data.getFullYear()
      }]),
      'inCreazione': isCreazione
    }

    try {
      const response = await lastValueFrom(this.http.post<EventoUpdResponse>(completeUrl,body));
      if (response && response.st == 0) {
        /**
         * In caso di inserimento evento, l'app comunica un idEvento fasullo, quindi occorre sostituirlo con quello vero
         */
        if (isCreazione) spesa.idRigoMovimentazione = response.nuovoIdEvento;
        /**
         * Upload allegati
         */
          if(spesa.allegati.length > 0)
            await this.uploadAllegatiEvento(userData, spesa);
      }
      else {
        throw Error(`Il server ha rifiutato l'upload della spesa`);
      }
      return true;
    } catch (error) {
      console.error(`Impossibile caricare la spesa ${spesa.idProdotto} su server. Da sincronizzare`, error);
      throw Error(`Impossibile caricare la spesa ${spesa.idProdotto} su server. Da sincronizzare`);
    }
  }

  async cancellaSpesa(spesa: Spesa): Promise<any> {
    const { server, aziCdazi, token } = await this.userService.getDatiUtente();
    const completeUrl = `${server}${this.DELETE_SPESA_ROUTE}`;
    const body = {
      aziCdazi: aziCdazi,
      token: token,
      idEvento: spesa.idRigoMovimentazione
    }

    try {
      const response = await lastValueFrom(this.http.post<EventoDelResponse>(completeUrl,body));
      if (response && response.st != 0) {
        throw Error(`Il server ha rifiutato la cancellazione della spesa`);
      }
      return true;
    } catch (error) {
      console.error(`Impossibile cancellare la spesa  ${spesa.idProdotto} su server. Da sincronizzare`, error);
      throw Error(`Impossibile cancellare la spesa ${spesa.idProdotto} su server. Da sincronizzare`);
    }
  }

  async uploadAllegatiEvento(userData: AppUser, spesa: Spesa,): Promise<any>{
    let completeUrl = `${userData.server}${this.INSUPDDEL_SPESA_ROUTE}`;
    const allegati = spesa.allegati;
    /**
     * Object destructuring assignment: 
     * - estraggo contenutoBase64Compresso e creo una copia di allegati senza quella proprietà;
     * - modifico contenutoBase64: elimino intestazione e sostituisco tutti gli 'spazi' (\\s) con dei '+' se presenti.
     */
    const allegatiSenzaCompresso = allegati.map(allegato => {
      const { contenutoBase64Compresso, contenutoBase64, ...allegatoSenzaCompresso } = allegato;
      const nuovoContenutoBase64 = contenutoBase64.split(";base64,")[1].replace(/\\s/, "+");
      return { ...allegatoSenzaCompresso, contenutoBase64: nuovoContenutoBase64 };
    });
    
    const body = {
      'aziCdazi': userData.aziCdazi,
      'token': userData.token,
      'codiceOperatore': userData.putSigla,
      'idIntervento': spesa.idIntervento,
      'progressivo': 0,
      'idEvento': spesa.idRigoMovimentazione,
      'SDTwsrEventiAllegatiJson': JSON.stringify(allegatiSenzaCompresso)
    };

    try{
      const response = await lastValueFrom(this.http.post<EventoUpdResponse>(completeUrl, body));
      if (response && response.st == 0)
        return true 
      else 
        throw new Error('Aggiornamento allegati spesa NON andato a buon fine');
    } catch (error) {
      console.error(`Impossibile caricare gli allegati per la spesa ${spesa.idRigoMovimentazione} su server. Da sincronizzare`, error);
      throw Error(`Impossibile caricare gli allegati per la spesa ${spesa.idRigoMovimentazione} su server. Da sincronizzare`);
    }
  }
}